<script setup lang="ts">
import { errorString } from '@/utils/helpers/rest-response';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { computed, onMounted, shallowRef } from 'vue';
import { useRouter } from '@/router/router';
import { required, useRules } from '@/composables/vuetify-validation';
import i18n from '@/localisation/i18n';
import { store as vuex } from '@/store/store';

const router = useRouter();
const snackbarManager = useSnackbarManager();

const props = withDefaults(
  defineProps<{
    loginEmail?: string;
    lostMessage?: string;
    secretCode?: string;
  }>(),
  { loginEmail: 'your inbox', lostMessage: '', secretCode: '' }
);

const clientConfig = computed(() => vuex.state.clientConfig!);

const emailAddress = shallowRef(props.loginEmail);
const resetCode = shallowRef(props.secretCode);
const tfaError = shallowRef('');
const isProcessing = shallowRef(false);

onMounted(async () => {
  if (resetCode.value) {
    await onSubmit();
  }
});

const onSubmit = () =>
  validateAndSubmit(async () => {
    if (isProcessing.value) return;
    isProcessing.value = true;

    try {
      await vuex.dispatch('reset2FALogin', resetCode.value);

      resetMessage(
        i18n.t('loginResetDeviceEmailSucceeded') as string,
        i18n.t('loginClose') as string
      );

      await router.push({ name: 'login' });
    } catch (e) {
      tfaError.value = errorString(e);
    } finally {
      isProcessing.value = false;
    }
  });

function resetMessage(message: string, actionText: string) {
  snackbarManager.show({ message: message, actionText: actionText });
}

const { rules, validateAndSubmit } = useRules<{ resetCode: string }>({
  resetCode: {
    rules: [
      () => ((tfaError.value = ''), true),
      (v) => required(v, `${i18n.t('resetCode')} ${i18n.t('formfield.IsRequired')}`),
    ],
  },
});
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="600"
    origin="center center"
    overlay-color="secondary"
    overlay-opacity="0.25"
    persistent
    transition="scale-transition"
    :value="true"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{
            $t('loginResetDeviceTitle', { title: clientConfig?.systemTitleShort })
          }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer class="mt-6">
            <VAlert v-if="tfaError" type="error">
              {{ tfaError }}
              <br />
              {{ $t('loginResetDeviceError2') }}
            </VAlert>
            <VAlert v-else border="left" color="warning" icon="mdi-information" text>
              {{ $t('loginResetDeviceInfo1', { title: clientConfig?.systemTitleShort }) }}
              <br />
              {{ $t('loginResetDeviceInfo2', { emailAddress: emailAddress }) }}
            </VAlert>
          </VContainer>

          <VContainer class="mt-6">
            <VTextField
              v-model="resetCode"
              v-bind="rules.resetCode"
              autofocus
              :label="$t('resetCode')"
              required
            >
            </VTextField>
          </VContainer>
        </VCardText>
        <VCardActions>
          <VBtn class="d-none d-sm-flex" color="secondary" text to="/login">
            {{ $t('loginButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            class="px-8 mx-4"
            color="primary"
            :disabled="isProcessing"
            :loading="isProcessing"
            type="submit"
            @click="onSubmit"
          >
            <VIcon left>mdi-shield-off</VIcon>
            {{ $t('loginResetDeviceButton') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>
